import { useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import { SELECTED_ITEMS } from 'constants/formFields';
import getNaceLabel from 'utils/getNaceLabel';

import type ISelectSupplierTable from './interfaces/IItemsTable';

import styles from './scss/ItemsTable.module.scss';

function ItemsTable({
  items,
  headCells,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInfoTable,
  naceCodeTreeData,
  control,
  onSelectItem,
  selectedItems,
  handleUnSelectAll,
  handleSelectTemplateItem,
  activeTab,
}: ISelectSupplierTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');

  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  return (
    <div className={styles.root}>
      <div
        className={styles.tableBodyScroll}
        ref={scrollRef}
        aria-label="Analysis table body"
        role="region"
      >
        <Table
          className={clsx(styles.table, {
            [styles.tableInfoTable]: isInfoTable,
          })}
        >
          <TableHead className={clsx(styles.header, {
            [styles.headerInfoTable]: isInfoTable,
          })}
          >
            {headCells.map(({
              id, label,
            }) => (
              <TableCell head className={styles.tableCell} key={id}>
                <div>
                  <span className={styles.tableCellLabel}>
                    {id === 'naceSector' ? (
                      <div className={styles.checkboxLabel}>
                        <div className={styles.checkboxColumn}>
                          <Checkbox
                            name="vs-all"
                            className={clsx(styles.checkbox, [selectedItems.length === 0 && styles.disabled])}
                            checked={selectedItems.length > 0}
                            value={String(selectedItems.length > 0)}
                            onChange={handleUnSelectAll}
                            label={false}
                            aria-label={t('common.select')}
                            disabled={selectedItems.length === 0}
                            isLineMark
                          />
                        </div>
                        {t(label)}
                      </div>
                    ) : t(label)}
                  </span>
                </div>
              </TableCell>
            ))}
          </TableHead>
          <TableBody className={styles.body}>
            {items ? items?.map((item, index) => (
              <TableRow key={[item.id, index].join('_')}>
                <TableCell>
                  <div className={styles.staticCell}>
                    <div className={styles.checkboxColumn}>
                      <Controller
                        control={control}
                        name={SELECTED_ITEMS}
                        render={({ field: { onChange, value, disabled } }) => (
                          <Checkbox
                            key={item.id}
                            name={`${SELECTED_ITEMS}-${item.id}`}
                            checked={!!value.includes(item.id)}
                            value={item.id}
                            disabled={disabled}
                            onChange={(event) => {
                              onChange(event.target.checked
                                ? [...value, item.id]
                                : value.filter((selectedItem) => selectedItem !== item.id));
                              onSelectItem({ id: item.id, isSelected: event.target.checked });
                              handleSelectTemplateItem?.(activeTab || 0, item.id);
                            }}
                            label={false}
                            isLineMark={false}
                          />
                        )}
                      />
                    </div>
                    <span>
                      {getNaceLabel(
                        {
                          naceSection: item.naceSection,
                          naceCodeTreeData,
                        },
                      ).naceSectionLabel || hyphen}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {getNaceLabel({
                    naceCode: item.naceCode,
                    naceSection: item.naceSection,
                    naceCodeTreeData,
                  }).naceCodeLabel || hyphen}
                </TableCell>
                <TableCell>
                  {item.topic.title || hyphen}
                </TableCell>
                <TableCell>
                  {item.category || hyphen}
                </TableCell>
                <TableCell>
                  {item.description || hyphen}
                </TableCell>
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ItemsTable;
